import { useEffect } from 'react';

import type { LuminaComponentShownAttributesType } from './generated/analytics.types';
import { hasShown, markShown } from './shown-event-cache';
import { useLuminaAnalyticsEvents } from './use-lumina-analytics-events';

type LuminaShownComponentType = LuminaComponentShownAttributesType['componentType'];

/**
 * Fires the `ui.luminaComponent.shown` event exactly once per `(sessionId, componentType)`.
 *
 * Lumina components render as streaming "status components" that mount/unmount
 * repeatedly while an answer streams in and whenever the surrounding message list
 * re-renders. Firing naively on mount would emit duplicate events; this hook
 * de-dupes via a module-level, SSR-safe, size-bounded cache (see
 * `shown-event-cache.ts`) so downstream consumers can rely on exactly one "shown"
 * event per component per answer.
 *
 * The event is intentionally NOT fired while `sessionId` is undefined (e.g. before
 * the final chat message id has settled during streaming), so it is never emitted
 * with an id that cannot be joined against the markdown "extracted" event.
 *
 * @example
 * ```tsx
 * const SmartLinkListElementAdapter: ElementRenderer = ({ sessionId }) => {
 *   useLuminaShownOnce({ componentType: 'smartLinkList', sessionId });
 *   return <LinkList ... />;
 * };
 * ```
 */
export const useLuminaShownOnce = ({
	componentType,
	sessionId,
}: {
	componentType: LuminaShownComponentType;
	sessionId: string | undefined;
}): void => {
	const { fireEvent } = useLuminaAnalyticsEvents();

	useEffect(() => {
		if (sessionId === undefined || hasShown(sessionId, componentType)) {
			return;
		}
		markShown(sessionId, componentType);
		fireEvent('ui.luminaComponent.shown', { componentType, sessionId });
	}, [componentType, sessionId, fireEvent]);
};
